import React from 'react';
import PropTypes from 'prop-types';
const uuid = require('uuid');
import { Card, Form, Alert, Button } from 'react-bootstrap';
const forEach = require('lodash/forEach');
const find = require('lodash/find');

const ServerRequest = require('../../actions/request');
const GlobalUserActions = require('../../actions/GlobalUserActions');
import OpenIdProviderSelector from './OpenIdProviderSelector';

const propTypes = {
    user: PropTypes.object.isRequired,
    deployment: PropTypes.object.isRequired,
    openIdProviders: PropTypes.array, // if a provider isn't found here, iss/description are read from the deployment record
    updateUrl: PropTypes.string, // defaults to the admin endpoint
    allowDelete: PropTypes.bool,
    onUpdated: PropTypes.func,
    onUnarchived: PropTypes.func, // (deploymentId)
    highlight: PropTypes.bool,
};

const defaultProps = {
    openIdProviders: [],
    allowDelete: true,
};

const columnInfo = {
    id: {
        title: 'ID',
        hidden: true
    },
    created_at: {
        title: 'Created At',
        hidden: true
    },
    op_id: {
        title: 'OpenId Provider ID',
        editable: true, // use OpenIdProviderSelector
    },
    iss: {
        title: 'Issuer',
        fromOp: true,
    },
    description: {
        title: 'Description',
        fromOp: true,
    },
    aud: { 
        title: 'Client ID (aud)',
        editable: true,
    },
//    platform_url: { 
//        title: 'Platform URL',
//        editable: true,
//    },
    deployment_id: { 
        title: 'Deployment ID',
        editable: true,
    },
    oidc_auth_url: { 
        title: 'OIDC Auth URL',
        editable: true,
    },
    access_token_url: {
        title: 'OAuth2 Access Token URL',
        editable: true,
    },
    jwks_url: { 
        title: 'JWKS URL',
        editable: true,
    },
    archived: {
        title: 'Archived',
        hidden: true
    },
};

class AdminUserLmsDeploymentEditor extends React.Component {
    constructor(props) {
        super(props);
        const op = find(props.openIdProviders, { id: String(props.deployment.op_id) });
        this.state = {
            ...this.getStateFromProps(props,op),
            canEdit: false,
            showAdvanced: false,
            anythingChanged: false,
            lmsDeploymentMessageVisible: false,
            lmsDeploymentMessageState: '',
            lmsDeploymentMessage: '',
        };
        this.rootRef = React.createRef();
        
    }
    
    getStateFromProps=(props, op)=>{
        if (!op) {
            op={};
        }
        var s = {};
        forEach(columnInfo,(v,k)=>{
            if (v.fromOp) {
                s[k] = op[k] !== undefined ? op[k] : props.deployment[k];
            }
            else {
                s[k] = props.deployment[k];
            }
        });
        s.options = { ...(props.deployment.options || {}) };
        return s;
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        var op;
        if (nextProps.deployment.op_id !== this.props.deployment.op_id) {
            // external change updated the op.
            op = find(nextProps.openIdProviders,{ id: String(nextProps.deployment.op_id) });
        }
        else {
            op = find(nextProps.openIdProviders,{ id: String(this.state.op_id) });
        }

        const sameDeployment = nextProps.deployment.id === this.props.deployment.id;
        this.setState ({
            ...this.getStateFromProps(nextProps, op),
            canEdit: false,
            anythingChanged: false,
            // keep the status message when this is just a refresh of the same record (e.g. after an update)
            ...(sameDeployment ? {} : {
                lmsDeploymentMessageVisible: false,
                lmsDeploymentMessageState: '',
                lmsDeploymentMessage: ''
            }),
        });
    }

    componentDidMount() {
        this.scrollIntoViewIfHighlighted();
    }

    componentDidUpdate(prevProps) {
        if (this.props.highlight && !prevProps.highlight) {
            this.scrollIntoViewIfHighlighted();
        }
    }

    // the parent highlights a just-unarchived deployment, which has moved to its place among the active ones
    scrollIntoViewIfHighlighted=()=>{
        if (this.props.highlight && this.rootRef.current) {
            this.rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    updateCanEdit=(e)=>{
        if (!this.state.canEdit) {
            this.setState({ canEdit: true, showAdvanced: false });
            return;
        }

        // locking: discard any unsaved edits (after confirming) so the locked view shows the saved values
        if (this.state.anythingChanged && !window.confirm("Discard your unsaved changes to this deployment?")) {
            return;
        }
        const op = find(this.props.openIdProviders, { id: String(this.props.deployment.op_id) });
        this.setState({
            ...this.getStateFromProps(this.props, op),
            canEdit: false,
            showAdvanced: false,
            anythingChanged: false,
        });
    }

    updateDeployment=(e)=>{
        const recordId = this.props.deployment.id;
        const changes = {
            op_id: this.state.op_id,
            platform_url: this.state.platform_url,
            deployment_id: this.state.deployment_id,
            aud: this.state.aud,
            oidc_auth_url: this.state.oidc_auth_url,
            access_token_url: this.state.access_token_url,
            jwks_url: this.state.jwks_url,
            options: this.state.options,
        };
        
        if (confirm("Are you sure you want to update the deployment data?")) {
            this.setState({ anythingChanged: false }, () => { // disable the update button while we're sending the update
                const updateUrl = this.props.updateUrl || `/authoring/user/${this.props.user.id}/update/lms_deployment`;
                const sendUpdate = (allowSharedClientId) => ServerRequest.makeRequest(updateUrl, 'POST', JSON.stringify({
                        userId: this.props.user.id,
                        recordId: recordId,
                        changes: changes,
                        allowSharedClientId,
                    }))
                    .catch((err) => {
                        // admin endpoint: 409 means the client id is used by another account; let the admin decide
                        if (err && err.status === 409 && !allowSharedClientId) {
                            if (confirm(`${err.responseText}\n\nUse this Client ID anyway?`)) {
                                return sendUpdate(true);
                            }
                            throw { cancelled: true }; // eslint-disable-line no-throw-literal
                        }
                        throw err;
                    });
                sendUpdate(false)
                    .then((res) => {
                        this.setState({
                            lmsDeploymentMessageVisible:true,
                            lmsDeploymentMessageState: 'success',
                            lmsDeploymentMessage: 'LMS Deployment Updated',
                        });
                        if (this.props.onUpdated) {
                            this.props.onUpdated();
                        }
                        return null; // the refresh is fire-and-forget
                    })
                    .catch((err) => {
                        if (err && err.cancelled) {
                            this.setState({ anythingChanged: true }); // nothing was saved; let them change it or try again
                            return;
                        }
                        console.log("error", err)
                        // the server sends 400 reasons (e.g. "That Client ID is already used by another organization") as plain text
                        let detail = null;
                        if (err && err.status === 400) {
                            detail = (err.responseJSON && err.responseJSON.error) || err.responseText;
                        }
                        this.setState({
                            lmsDeploymentMessageVisible:true,
                            lmsDeploymentMessageState: 'danger',
                            lmsDeploymentMessage: 'There was a problem updating the LMS Deployment' + (typeof detail === 'string' ? `: ${detail}` : ''),
                            anythingChanged: true, // let them retry
                        });
                    });
            });
        }
    }

    deleteDeployment=(e)=>{
        if (confirm("Archive this LMS deployment? LMS launches from it will stop working, but it can be unarchived later.")) {
            ServerRequest.makeRequest((`/authoring/user/${this.props.user.id}/delete/lms_deployment`), 'POST', JSON.stringify({
                    recordId: this.props.deployment.id
                }))
                .then((res) => {
                    this.refreshList();
                    return null;
                })
                .catch((err) => { console.log("error", err); });
        }
    }

    unarchiveDeployment=(e)=>{
        if (confirm("Are you sure you want to unarchive this LMS deployment? It will become active again.")) {
            ServerRequest.makeRequest((`/authoring/user/${this.props.user.id}/unarchive/lms_deployment`), 'POST', JSON.stringify({
                    recordId: this.props.deployment.id
                }))
                .then((res) => {
                    if (this.props.onUnarchived) {
                        this.props.onUnarchived(this.props.deployment.id);
                    }
                    this.refreshList();
                    return null;
                })
                .catch((err) => {
                    console.log("error", err);
                    this.setState({
                        lmsDeploymentMessageVisible: true,
                        lmsDeploymentMessageState: 'danger',
                        lmsDeploymentMessage: 'There was a problem unarchiving the LMS Deployment',
                    });
                });
        }
    }

    refreshList=()=>{
        if (this.props.onUpdated) {
            this.props.onUpdated();
        }
        else {
            GlobalUserActions.getLmsDeployments(uuid.v4(), this.props.user.id);
        }
    }
    
    onChange=(key, value)=>{
        const changes = {};
        changes[key] = value;
        let anythingChanged = false;
        forEach(columnInfo,(v,k)=>{
            if (v.editable) {
                const state_val = (key === k) ? value : this.state[k];
                if (this.props.deployment[k] !== state_val) {
                    anythingChanged = true;
                }
            }
        })
        changes.anythingChanged = anythingChanged;
        this.setState(changes);
    }

    onOptionsChange=(key, value)=>{
        this.setState({
            options: { ...this.state.options, [key]: value },
            anythingChanged: true,
        });
    }

    render() {
        var entries = [];
        let formStyle={};
        if (this.state.canEdit) {
            formStyle = { display: 'block' };
        }
        forEach(columnInfo,(v, k)=>{
            if (!v.hidden) {
                if (k === 'op_id') {
                    if (this.state.openIdProviders && this.state.openIdProviders.length) {
                        entries.push(
                            <OpenIdProviderSelector key={k}
                                id={`admin-lms-deployment-op-id-selector-${this.props.deployment.id}`}
                                openIdProviders={this.state.openIdProviders}
                                onSelect={(val)=>{this.onChange('op_id',val)}}
                            />
                        );
                    }
                }
                else {
                    const value = this.state[k] || '';
                    entries.push (
                        // when locked, fields share one line and truncate; the title shows the full value
                        <Form.Group key={k} controlId={`admin-${k}-${this.props.deployment.id}`} className="lms-field" style={formStyle} title={this.state.canEdit ? undefined : value}>
                            <Form.Label>{v.title}</Form.Label>
                            <Form.Control
                                type="text"
                                className="min-width-url"
                                aria-label={v.title}
                                value={value}
                                onChange={(e)=>{ if (v.editable) { this.onChange(k,e.target.value)} } }
                                disabled={!this.state.canEdit || !v.editable}
                            />
                        </Form.Group>
                    );
                }
            }
        })
        
        const lockButton = (
            <Button className="lms-lock-button" onClick={this.updateCanEdit} aria-label={this.state.canEdit ? 'Lock' : 'Unlock to edit'} title={this.state.canEdit ? 'Lock' : 'Unlock to edit'}>
                <i className={!this.state.canEdit ? 'fa fa-lock' : 'fa fa-unlock-alt'} aria-hidden="true"></i>
            </Button>
        );
        const archived = !!this.props.deployment.archived;
        // archived deployments are read-only: they show an Archived tag instead of the lock button, and Unarchive (admin) instead of Delete
        const leadingItem = archived ?
            <span className="lms-archived-tag">Archived</span>
            : lockButton;
        let deleteButton = null;
        if (this.props.allowDelete) {
            deleteButton = archived ?
                <Button className="lms-delete-button" onClick={this.unarchiveDeployment}>Unarchive</Button>
                :
                <Button className="lms-delete-button" onClick={this.deleteDeployment}>Archive</Button>;
        }

        return (
            <div ref={this.rootRef} className={'lms-deployment-editor' + (archived ? ' archived' : '') + (this.props.highlight ? ' just-unarchived' : '')}>
                <Card body bg="light">
                    {/* locked: lock button, fields (and admin Delete) all on one line; unlocked: fields stacked with the buttons below */}
                    <div className={'lms-deployment-fields' + (this.state.canEdit ? '' : ' locked')}>
                        {!this.state.canEdit && leadingItem}
                        {entries}
                        {!this.state.canEdit && deleteButton}
                    </div>

                    {(this.state.canEdit && this.state.showAdvanced) &&
                    <Form.Group
                        controlId={`admin-lineitem-tag-options-${this.props.deployment.id}`}
                        style={{ ...formStyle, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75em' }}
                    >
                        <Form.Label
                            className="mb-0"
                            title={'Prefixes score category tags (e.g. "1" becomes "<prefix>1") when searching for/creating lineitems. Leave blank unless this platform\'s own default lineitem collides with a bare category tag.'}
                        >
                            Tag Prefix
                        </Form.Label>
                        <Form.Control
                            type="text"
                            size="sm"
                            style={{ width: '8em' }}
                            aria-label="Score Category Tag Prefix"
                            value={this.state.options.tag_prefix || ''}
                            onChange={(e)=>{ this.onOptionsChange('tag_prefix', e.target.value); }}
                            disabled={!this.state.canEdit}
                        />
                        <Form.Check
                            className="mb-0"
                            type="checkbox"
                            id={`admin-lineitem-fix-collision-${this.props.deployment.id}`}
                            label="Auto-correct default/category tag collisions"
                            title={'When a score category resolves to the same lineitem as the platform\'s default, retag the shared lineitem to "grade" and create a separate lineitem for that category. Safe to disable once the platform-side data no longer needs correcting.'}
                            checked={!!this.state.options.fix_default_lineitem_tag_collision}
                            onChange={(e)=>{ this.onOptionsChange('fix_default_lineitem_tag_collision', e.target.checked); }}
                            disabled={!this.state.canEdit}
                        />
                        <Form.Check
                            className="mb-0"
                            type="checkbox"
                            id={`admin-lineitem-ensure-grade-${this.props.deployment.id}`}
                            label="Ensure dedicated 'grade' default lineitem"
                            title={'Some platforms adopt whatever lineitem we create for a score category as their own reported default on later launches, which then collides with that category. When enabled, use (or create, if missing) a lineitem tagged "grade" as our default instead of trusting the platform-reported one.'}
                            checked={!!this.state.options.ensure_grade_lineitem}
                            onChange={(e)=>{ this.onOptionsChange('ensure_grade_lineitem', e.target.checked); }}
                            disabled={!this.state.canEdit}
                        />
                    </Form.Group>
                    }

                    {this.state.canEdit &&
                        <div className="lms-deployment-controls">
                            {lockButton}

                            <Button onClick={this.updateDeployment} disabled={!this.state.anythingChanged}>
                                Update
                            </Button>

                            {deleteButton}

                            <Button
                                variant="link"
                                aria-expanded={this.state.showAdvanced}
                                onClick={() => this.setState({ showAdvanced: !this.state.showAdvanced })}
                            >
                                <i className={this.state.showAdvanced ? 'fa fa-caret-down' : 'fa fa-caret-right'} aria-hidden="true"></i> Advanced
                            </Button>
                        </div>
                    }
                </Card>
                { this.state.lmsDeploymentMessageVisible &&
                    <Alert variant={this.state.lmsDeploymentMessageState}>
                        <h4>{this.state.lmsDeploymentMessage}</h4>
                    </Alert>
                }
            </div>
        );
    }
}

AdminUserLmsDeploymentEditor.propTypes = propTypes;
AdminUserLmsDeploymentEditor.defaultProps = defaultProps;

export default AdminUserLmsDeploymentEditor;